import { DEFAULT_LOCALE, Locale } from '@longy/common'
import { CmsRendererTranslations, getCmsTranslations } from '@longy/common/cms'
import { createContext, useContext } from 'react'

/**
 * The single injection port. Everything environmental a renderer could need arrives through this
 * one context, which is what lets the same component tree serve server rendering, the admin's
 * iframe preview and a static render with no branching anywhere inside it.
 *
 * The reference carries considerably more here — a host mapping for multi-domain serving, a brand
 * theme, a query proxy for blocks that paginate themselves. None of those subsystems are part of
 * this port, and carrying inert fields for them would only invite someone to start reading them.
 */
export type RendererContextValue = {
    /** Empty for relative hrefs; a full origin when links must leave an iframe correctly. */
    baseUrl: string
    openLinksInNewTab: boolean
    /**
     * Side effects only, never visuals. A preview that renders differently from the live page has
     * stopped being a preview; anything visual gets its own named prop.
     */
    isPreview: boolean
    locale: Locale
    /** Static copy for controls the blocks draw themselves. Derived from `locale`, never passed. */
    translations: CmsRendererTranslations
    /** Where overlays mount. In the preview this is the iframe body, not the host document. */
    portalContainer: HTMLElement | null
}

/** Every default means inert, never broken. */
export const rendererContextDefault: RendererContextValue = {
    baseUrl: '',
    openLinksInNewTab: false,
    // Defaults to "this is real": a surface that forgets to opt in gets live behaviour rather than
    // a silently faked one.
    isPreview: false,
    locale: DEFAULT_LOCALE,
    translations: getCmsTranslations(DEFAULT_LOCALE),
    portalContainer: null,
}

export const RendererContext = createContext<RendererContextValue>(rendererContextDefault)

export const useRendererContext = (): RendererContextValue => useContext(RendererContext)

// Narrow hooks rather than the whole context, so what a renderer depends on is visible in its
// imports.
export const useRendererLocale = (): Locale => useRendererContext().locale
export const useIsPreview = (): boolean => useRendererContext().isPreview
export const useCmsTranslations = (): CmsRendererTranslations => useRendererContext().translations
export const usePortalContainer = (): HTMLElement | null => useRendererContext().portalContainer

export type RendererProviderProps = Partial<Omit<RendererContextValue, 'translations'>> & {
    children: React.ReactNode
}

export const RendererProvider = ({ children, ...overrides }: RendererProviderProps) => {
    const parent = useRendererContext()
    const locale = overrides.locale ?? parent.locale

    return (
        <RendererContext.Provider
            value={{ ...parent, ...overrides, locale, translations: getCmsTranslations(locale) }}
        >
            {children}
        </RendererContext.Provider>
    )
}
